import { useState, useRef, useEffect, createContext, useContext } from 'react'
import html2pdf from 'html2pdf.js'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { buildReportData, reportFactsForAI } from '../lib/reportData'
const fmtMoney = (n) => (n || n === 0) ? '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }) : '—'

// Report editing context: lets any section be removed / restored before sending
const RepCtx = createContext({ editing: false, hidden: {}, toggle: () => {} })

function Sec({ id, children }) {
  const { editing, hidden, toggle } = useContext(RepCtx)
  const isHidden = !!hidden[id]
  if (isHidden && !editing) return null
  return (
    <div style={{ position: 'relative', opacity: isHidden ? 0.35 : 1 }}>
      {editing && (
        <button
          onClick={() => toggle(id)}
          style={{
            position: 'absolute', right: 0, top: 14, zIndex: 2,
            fontSize: 10.5, fontWeight: 700, padding: '3px 9px', borderRadius: 6, cursor: 'pointer',
            border: '1px solid ' + (isHidden ? 'var(--green)' : 'var(--line-2)'),
            background: isHidden ? 'var(--green-soft)' : '#fff',
            color: isHidden ? 'var(--green)' : 'var(--muted)',
          }}
        >{isHidden ? 'Restore' : 'Remove'}</button>
      )}
      {children}
    </div>
  )
}

export default function Reports() {
  const { config } = useAuth()
  const [kind, setKind] = useState('owner')          // 'owner' | 'management'
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7))
  const [data, setData] = useState(null)
  const [aiIntro, setAiIntro] = useState('')
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [editing, setEditing] = useState(false)
  const [hidden, setHidden] = useState({})
  const [note, setNote] = useState('')
  const toggle = (id) => setHidden(h => ({ ...h, [id]: !h[id] }))
  const [err, setErr] = useState('')
  const paperRef = useRef(null)
  // a report requested from the AI chat: preset kind + month, then generate once
  const [autoGen, setAutoGen] = useState(false)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('hc_report_request'); if (!raw) return
      localStorage.removeItem('hc_report_request')
      const rq = JSON.parse(raw)
      if (rq.kind === 'owner' || rq.kind === 'management') setKind(rq.kind)
      if (/^\d{4}-\d{2}$/.test(rq.month || '')) setMonth(rq.month)
      setAutoGen(true)
    } catch (e) {}
  }, [])
  useEffect(() => { if (autoGen && config) { setAutoGen(false); generate() } }, [autoGen, config]) // eslint-disable-line

  async function generate() {
    setLoading(true); setErr(''); setData(null); setAiIntro(''); setHidden({}); setNote(''); setEditing(false)
    try {
      const d = await buildReportData({ config, month })
      setData(d)
      // AI intro — narrates the precomputed numbers only (report_summary action)
      try {
        const facts = reportFactsForAI(d, kind)
        const { data: { session } } = await supabase.auth.getSession()
        const res = await fetch('/api/ai', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (session?.access_token || '') },
          body: JSON.stringify({ action: 'report_summary', facts, kind }),
        })
        const j = await res.json().catch(() => null)
        if (j?.result) setAiIntro(String(j.result).trim())
      } catch { /* AI intro is best-effort */ }
    } catch (e) {
      setErr('Could not build the report. ' + (e?.message || ''))
    } finally { setLoading(false) }
  }

  async function downloadPdf() {
    if (!paperRef.current) return
    setEditing(false)
    await new Promise(r => setTimeout(r, 60))
    setExporting(true)
    try {
      const name = (data?.meta?.vessel || 'vessel').replace(/[^a-z0-9]/gi, '_') + '_' + kind + '_' + month + '.pdf'
      const opt = { margin: 0, filename: name, image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', windowWidth: 900 }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }, pagebreak: { mode: ['avoid-all', 'css'] } }
      await html2pdf().set(opt).from(paperRef.current).save()
    } finally { setExporting(false) }
  }

  return (
    <div style={{ padding: '18px 22px 60px', maxWidth: 1000, margin: '0 auto' }}>
      {/* Controls */}
      <div style={ctrl.bar}>
        <div style={ctrl.seg}>
          <button onClick={() => setKind('owner')} style={{ ...ctrl.segBtn, ...(kind === 'owner' ? ctrl.segOn : {}) }}>Owner report</button>
          <button onClick={() => setKind('management')} style={{ ...ctrl.segBtn, ...(kind === 'management' ? ctrl.segOn : {}) }}>Management / DPA</button>
        </div>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={ctrl.month} />
        <button onClick={generate} disabled={loading} style={ctrl.gen}>{loading ? 'Building…' : 'Generate'}</button>
        {data && <button onClick={() => setEditing(e => !e)} style={{ ...ctrl.seg, ...ctrl.segBtn, ...(editing ? ctrl.segOn : {}), marginLeft: 'auto' }}>{editing ? 'Done editing' : 'Edit report'}</button>}
        {data && <button onClick={downloadPdf} disabled={exporting} style={{ ...ctrl.pdf, marginLeft: 8 }}>{exporting ? 'Exporting…' : 'Download PDF'}</button>}
      </div>

      {err && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{err}</div>}

      {data && editing && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 4 }}>Captain&rsquo;s note (appears at the end of the report)</div>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Add context for the owner — e.g. why an item is outstanding, or what was resolved."
            style={{ width: '100%', padding: '9px 11px', fontSize: 13, fontFamily: 'inherit', lineHeight: 1.5, border: '1px solid var(--line-2)', borderRadius: 9, resize: 'vertical' }} />
        </div>
      )}

      {!data && !loading && (
        <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '80px 20px', fontSize: 14 }}>
          Choose a report type and month, then Generate. The report gathers live figures from every module — the AI summary only narrates them.
        </div>
      )}

      {/* Report paper */}
      {data && (
        <RepCtx.Provider value={{ editing, hidden, toggle }}>
          <div ref={paperRef} style={paper.sheet}>
            {kind === 'owner'
              ? <OwnerReport d={data} intro={aiIntro} setIntro={setAiIntro} note={note} />
              : <ManagementReport d={data} intro={aiIntro} setIntro={setAiIntro} note={note} />}
          </div>
        </RepCtx.Provider>
      )}
    </div>
  )
}

// ─────────────────────────── OWNER REPORT ───────────────────────────
function OwnerReport({ d, intro, setIntro, note }) {
  const b = d.budget
  const tiles = [
    { label: 'Equipment', val: d.equipment.outOfService === 0 ? 'All operational' : d.equipment.outOfService + ' out of service', ok: d.equipment.outOfService === 0 },
    { label: 'Certificates', val: d.documents.expired === 0 ? 'All valid' : d.documents.expired + ' expired', ok: d.documents.expired === 0 },
    { label: 'Safety (LSA/FFE)', val: d.lsa.defect === 0 ? 'No defects' : d.lsa.defect + ' defects', ok: d.lsa.defect === 0 },
    { label: 'Maintenance', val: d.maintenance.overdue === 0 ? 'On schedule' : d.maintenance.overdue + ' overdue', ok: d.maintenance.overdue === 0 },
  ]
  return (
    <>
      <Header d={d} title="Owner’s Report" accent="var(--gold2)" />
      <IntroBlock intro={intro} setIntro={setIntro} />

      <Sec id="status">
      <SectionTitle>Status at a glance</SectionTitle>
      <div style={paper.tileRow}>
        {tiles.map((t, i) => (
          <div key={i} style={{ ...paper.tile, borderColor: t.ok ? 'var(--green)' : 'var(--red)' }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{t.label}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: t.ok ? 'var(--green)' : 'var(--red)', marginTop: 4 }}>{t.val}</div>
          </div>
        ))}
      </div>
      </Sec>

      <Sec id="been">
      <SectionTitle>Where she’s been</SectionTitle>
      <div style={paper.kvRow}>
        <KV label="Port calls" val={d.portCalls.count} />
        <KV label="Charter days" val={d.charters.days} />
        <KV label="Charters" val={d.charters.count} />
      </div>
      {d.portCalls.list.length > 0 && (
        <ul style={paper.ul}>{d.portCalls.list.slice(0, 8).map((p, i) => <li key={i}>{p.port}{p.eta ? ' — ' + String(p.eta).slice(0, 10) : ''}</li>)}</ul>
      )}
      </Sec>

      <EngineSection d={d} />

      <Sec id="finances">
      <SectionTitle>Finances</SectionTitle>
      <div style={paper.kvRow}>
        <KV label="Spent this month" val={fmtMoney(b.spentThisMonth)} />
        <KV label="Monthly budget" val={fmtMoney(b.monthlyBudget)} />
        <KV label="Year to date" val={fmtMoney(b.ytdSpent)} />
      </div>
      {b.spentThisMonth > 0 && (
        <div style={{ marginTop: 8 }}>
          {Object.entries(b.byCategory).filter(([, v]) => v > 0).slice(0, 8).map(([cat, v]) => (
            <BudgetBar key={cat} label={cat} value={v} max={Math.max(b.monthlyBudget, b.spentThisMonth)} />
          ))}
        </div>
      )}
      {d.charters.revenue > 0 && <div style={{ marginTop: 8, fontSize: 13 }}>Charter revenue this month: <b>{fmtMoney(d.charters.revenue)}</b></div>}
      </Sec>

      <Sec id="maint">
      <SectionTitle>Maintenance</SectionTitle>
      <div style={paper.kvRow}>
        <KV label="Completed / due this month" val={d.maintenance.dueThisMonth} />
        <KV label="Overdue" val={d.maintenance.overdue} tone={d.maintenance.overdue ? 'red' : 'green'} />
      </div>
      {d.maintenance.upcomingList.length > 0 && (
        <ul style={paper.ul}>{d.maintenance.upcomingList.slice(0, 8).map((t, i) => <li key={i}>{t}</li>)}</ul>
      )}
      </Sec>

      <CrewSection d={d} />
      <SurveysDue d={d} />
      <ProjectsSection d={d} />
      <LookingAhead d={d} />
      <NoteBlock note={note} />

      <Footer d={d} />
    </>
  )
}

// ──────────────── MANAGEMENT REPORT ────────────────
function ManagementReport({ d, intro, setIntro, note }) {
  const rows = [
    { k: 'Equipment out of service', v: d.equipment.outOfService, bad: d.equipment.outOfService > 0 },
    { k: 'Maintenance overdue', v: d.maintenance.overdue, bad: d.maintenance.overdue > 0 },
    { k: 'Certificates expired', v: d.documents.expired, bad: d.documents.expired > 0 },
    { k: 'Certificates expiring soon', v: d.documents.expiringSoon, bad: d.documents.expiringSoon > 0 },
    { k: 'Inventory out of stock', v: d.inventory.outOfStock, bad: d.inventory.outOfStock > 0 },
    { k: 'Risk assessments overdue', v: d.risk.overdue, bad: d.risk.overdue > 0 },
    { k: 'LSA / FFE defects', v: d.lsa.defect, bad: d.lsa.defect > 0 },
    { k: 'Drills completed this month', v: d.drills.thisMonth, bad: false },
  ]
  return (
    <>
      <Header d={d} title="Management / DPA Report" accent="var(--blue)" />
      <IntroBlock intro={intro} setIntro={setIntro} />

      <Sec id="compliance">
      <SectionTitle>Compliance summary</SectionTitle>
      <table style={paper.table}>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={paper.td}>{r.k}</td>
              <td style={{ ...paper.tdNum, color: r.bad ? 'var(--red)' : 'var(--green)', fontWeight: 700 }}>{r.v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </Sec>

      <Sec id="drills">
      <SectionTitle>Drill compliance ({d.meta.monthLabel})</SectionTitle>
      <div style={{ fontSize: 13 }}>
        {d.drills.types.length ? d.drills.types.join(', ') : 'No drills recorded this month.'}
      </div>
      </Sec>

      <Sec id="review">
      <SectionTitle>Monthly review — completed items</SectionTitle>
      {(d.compliance?.sections || []).map((s, si) => {
        const done = s.rows.filter(r => d.compliance.detected?.[r.key]?.done)
        if (!done.length) return null
        return (
          <div key={si} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--blue)', marginBottom: 2 }}>{s.group}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text)' }}>{done.map(r => r.label).join(' · ')}</div>
          </div>
        )
      })}
      </Sec>

      <CrewSection d={d} />
      <SurveysDue d={d} />
      <EngineSection d={d} />

      <ProjectsSection d={d} />
      <LookingAhead d={d} />

      <NoteBlock note={note} />

      {/* Sign-off */}
      <div style={paper.signRow}>
        <div style={paper.signBox}><div style={paper.signLine} /><div style={paper.signLbl}>Captain</div></div>
        <div style={paper.signBox}><div style={paper.signLine} /><div style={paper.signLbl}>DPA / Management</div></div>
      </div>

      <Footer d={d} />
    </>
  )
}

// ─────────────────────────── shared bits ───────────────────────────
function EngineSection({ d }) {
  const e = d.engine
  if (!e || (!e.totalHours && !e.fuel)) return null
  return (
    <Sec id="engine">
      <SectionTitle>Engine hours &amp; fuel</SectionTitle>
      <div style={paper.kvRow}>
        <KV label="Total hours run" val={e.totalHours} />
        {e.fuel > 0 && <KV label="Fuel used" val={e.fuel.toLocaleString('en-US')} />}
        <KV label="Log entries" val={e.entries} />
      </div>
      {e.run.length > 0 && (
        <ul style={paper.ul}>
          {e.run.map((r, i) => <li key={i}>{r.label} &mdash; {r.hours} h</li>)}
        </ul>
      )}
    </Sec>
  )
}

function SurveysDue({ d }) {
  const s = d.surveysDue
  if (!s || !s.total) return null
  return (
    <Sec id="surveys">
      <SectionTitle>Certificates &amp; surveys coming due</SectionTitle>
      <div style={paper.kvRow}>
        <KV label="Due within 12 months" val={s.total} />
        <KV label="Within 90 days" val={s.within90} tone={s.within90 ? 'red' : 'green'} />
      </div>
      <table style={{ ...paper.table, marginTop: 8 }}>
        <tbody>
          {s.list.map((x, i) => (
            <tr key={i}>
              <td style={paper.td}>{x.title}{x.type ? <span style={{ color: 'var(--muted)' }}> &middot; {x.type}</span> : null}</td>
              <td style={{ ...paper.tdNum, width: 96 }}>{x.date}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Sec>
  )
}

function CrewSection({ d }) {
  const c = d.crew
  if (!c || !(c.headcount || c.records)) return null
  const roles = Object.entries(c.byPosition || {}).sort((a, b) => b[1] - a[1])
  return (
    <Sec id="crew">
      <SectionTitle>Crew</SectionTitle>
      <div style={paper.kvRow}><KV label="Onboard" val={c.headcount} />{c.records != null && c.records !== c.headcount && <KV label="Crew records" val={c.records} />}</div>
      {roles.length > 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--text)', marginTop: 4 }}>
          {roles.map(([role, n], i) => (
            <span key={role}>{i > 0 ? ' \u00b7 ' : ''}{role} ({n})</span>
          ))}
        </div>
      )}
    </Sec>
  )
}

function ProjectsSection({ d }) {
  const p = d.projects || { list: [] }
  const q = d.quotes || {}
  if (!p.active && !p.completedThisMonth && !(q.quoteCount || 0)) return null
  return (
    <Sec id="projects">
      <SectionTitle>Projects &amp; quotes</SectionTitle>
      <div style={paper.kvRow}>
        <KV label="In progress" val={p.active} />
        <KV label="Quoted" val={fmtMoney(p.quotedTotal)} />
        <KV label="Approved" val={fmtMoney(p.approvedTotal)} />
        <KV label="Invoiced to date" val={fmtMoney(p.invoicedTotal)} />
      </div>
      {p.list.length > 0 && (
        <table style={{ ...paper.table, marginTop: 10 }}>
          <thead>
            <tr>
              <th style={paper.th}>Project</th>
              <th style={paper.th}>Contractor</th>
              <th style={paper.th}>Status</th>
              <th style={{ ...paper.th, textAlign: 'right' }}>Quoted</th>
              <th style={{ ...paper.th, textAlign: 'right' }}>Approved</th>
              <th style={{ ...paper.th, textAlign: 'right' }}>Invoiced</th>
            </tr>
          </thead>
          <tbody>
            {p.list.map((r, i) => (
              <tr key={i}>
                <td style={paper.td}>{r.name}{r.location ? <span style={{ color: 'var(--muted)' }}> &middot; {r.location}</span> : null}</td>
                <td style={paper.td}>{r.contractor || '\u2014'}</td>
                <td style={paper.td}>{r.status || '\u2014'}</td>
                <td style={paper.tdNum}>{r.quoted ? fmtMoney(r.quoted) : '\u2014'}</td>
                <td style={paper.tdNum}>{r.approved ? fmtMoney(r.approved) : '\u2014'}</td>
                <td style={paper.tdNum}>{r.invoiced ? fmtMoney(r.invoiced) : '\u2014'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(q.recentQuotes || []).length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 3 }}>Quotes on file ({q.quoteCount})</div>
          <ul style={paper.ul}>
            {q.recentQuotes.slice(0, 6).map((x, i) => (
              <li key={i}>{x.title}{x.issued ? ' \u2014 ' + String(x.issued).slice(0, 10) : ''}</li>
            ))}
          </ul>
        </div>
      )}
    </Sec>
  )
}

function LookingAhead({ d }) {
  const a = d.lookingAhead
  if (!a) return null
  const nothing = !a.maintenanceDue && !a.charters.length && !a.projects.length
  return (
    <Sec id="ahead">
      <SectionTitle>Looking ahead &mdash; {a.nextMonthLabel}</SectionTitle>
      {nothing && <div style={{ fontSize: 13, color: 'var(--muted)' }}>Nothing scheduled yet for next month.</div>}
      <div style={paper.kvRow}>
        {a.maintenanceDue > 0 && <KV label="Maintenance due" val={a.maintenanceDue} />}
        {a.charters.length > 0 && <KV label="Charters booked" val={a.charters.length} />}
        {a.projects.length > 0 && <KV label="Projects targeted" val={a.projects.length} />}
      </div>
      {a.charters.length > 0 && (
        <ul style={paper.ul}>
          {a.charters.map((c, i) => (
            <li key={i}>{c.ref}{c.start ? ' \u2014 ' + String(c.start).slice(0, 10) : ''}{c.end ? ' to ' + String(c.end).slice(0, 10) : ''}</li>
          ))}
        </ul>
      )}
      {a.projects.length > 0 && (
        <ul style={paper.ul}>
          {a.projects.map((p, i) => (
            <li key={i}>{p.name}{p.target ? ' \u2014 target ' + String(p.target).slice(0, 10) : ''}{p.quoted ? ' \u00b7 ' + fmtMoney(p.quoted) : ''}</li>
          ))}
        </ul>
      )}
      {a.maintenanceList.length > 0 && (
        <ul style={paper.ul}>{a.maintenanceList.slice(0, 6).map((t, i) => <li key={i}>{t}</li>)}</ul>
      )}
    </Sec>
  )
}

function IntroBlock({ intro, setIntro }) {
  const { editing } = useContext(RepCtx)
  if (!intro && !editing) return null
  return (
    <Sec id="intro">
      {editing
        ? <textarea value={intro} onChange={e => setIntro(e.target.value)} rows={5}
            style={{ ...paper.intro, width: '100%', fontFamily: 'inherit', border: '1px solid var(--line-2)', resize: 'vertical' }} />
        : <p style={paper.intro}>{intro}</p>}
    </Sec>
  )
}

function NoteBlock({ note }) {
  if (!note || !String(note).trim()) return null
  return (
    <Sec id="note">
      <SectionTitle>Captain&rsquo;s note</SectionTitle>
      <p style={{ fontSize: 13, lineHeight: 1.65, whiteSpace: 'pre-wrap', margin: 0 }}>{note}</p>
    </Sec>
  )
}

function Header({ d, title, accent }) {
  return (
    <div style={{ borderBottom: '2px solid ' + accent, paddingBottom: 12, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)', letterSpacing: '.01em' }}>{d.meta.vessel}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>{title} · {d.meta.monthLabel}</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--muted)' }}>
          {d.meta.flag && <div>Flag: {d.meta.flag}</div>}
          {d.meta.imo && <div>IMO: {d.meta.imo}</div>}
          <div>Generated {new Date(d.meta.generatedAt).toLocaleDateString('en-GB')}</div>
        </div>
      </div>
    </div>
  )
}
function Footer({ d }) {
  return <div style={{ marginTop: 24, paddingTop: 10, borderTop: '1px solid var(--line)', fontSize: 10, color: 'var(--muted)', textAlign: 'center' }}>
    Generated by Hey Captain · {d.meta.vessel} · {d.meta.monthLabel} · Figures computed from live vessel records.
  </div>
}
function SectionTitle({ children }) {
  return <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--navy)', margin: '18px 0 8px' }}>{children}</div>
}
function KV({ label, val, tone }) {
  return <div style={paper.kv}>
    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 800, color: tone === 'red' ? 'var(--red)' : tone === 'green' ? 'var(--green)' : 'var(--navy)' }}>{val}</div>
  </div>
}
function BudgetBar({ label, value, max }) {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0
  return <div style={{ margin: '5px 0' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 2 }}><span>{label}</span><span style={{ fontWeight: 700 }}>{fmtMoney(value)}</span></div>
    <div style={{ height: 6, background: 'var(--line)', borderRadius: 4 }}><div style={{ width: pct + '%', height: '100%', background: 'var(--gold2)', borderRadius: 4 }} /></div>
  </div>
}

const ctrl = {
  bar: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' },
  seg: { display: 'inline-flex', border: '1px solid var(--line-2)', borderRadius: 9, overflow: 'hidden' },
  segBtn: { padding: '8px 14px', fontSize: 13, fontWeight: 600, border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer' },
  segOn: { background: 'var(--navy)', color: '#fff' },
  month: { padding: '8px 10px', fontSize: 13, border: '1px solid var(--line-2)', borderRadius: 9, fontFamily: 'inherit' },
  gen: { padding: '8px 18px', fontSize: 13, fontWeight: 700, border: 'none', borderRadius: 9, background: 'var(--blue)', color: '#fff', cursor: 'pointer' },
  pdf: { padding: '8px 16px', fontSize: 13, fontWeight: 700, border: '1px solid var(--gold2)', borderRadius: 9, background: 'var(--gold-soft)', color: 'var(--gold3)', cursor: 'pointer', marginLeft: 'auto' },
}
const paper = {
  sheet: { background: '#fff', border: '1px solid var(--line)', borderRadius: 10, padding: '32px 36px', boxShadow: '0 4px 24px rgba(0,0,0,.06)', color: 'var(--text)', fontSize: 13, lineHeight: 1.55 },
  intro: { fontSize: 14, lineHeight: 1.7, color: 'var(--text)', background: 'var(--bg)', borderRadius: 8, padding: '12px 14px', margin: '0 0 4px' },
  tileRow: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 },
  tile: { border: '1px solid', borderRadius: 8, padding: '10px 12px', background: '#fff' },
  kvRow: { display: 'flex', gap: 28, flexWrap: 'wrap' },
  kv: { minWidth: 120 },
  ul: { margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, color: 'var(--text)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '5px 4px', borderBottom: '1px solid var(--line-2)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--muted)', textAlign: 'left', fontWeight: 700 },
  td: { padding: '7px 4px', borderBottom: '1px solid var(--line)', fontSize: 13 },
  tdNum: { padding: '7px 4px', borderBottom: '1px solid var(--line)', textAlign: 'right', width: 60, fontSize: 14 },
  signRow: { display: 'flex', gap: 40, marginTop: 32 },
  signBox: { flex: 1 },
  signLine: { borderBottom: '1px solid var(--navy)', height: 34 },
  signLbl: { fontSize: 11, color: 'var(--muted)', marginTop: 4 },
}
